import React from 'react';
import { SiGoogledrive, SiDropbox } from 'react-icons/si';
import { FaMicrosoft, FaYandex } from 'react-icons/fa';
import { CloudIcon } from '@heroicons/react/24/outline';
import {
  PROVIDER_LABELS,
  type StorageProvider,
  type StorageStatus,
} from '../../services/storageService';

export const EXTERNAL_PROVIDERS: StorageProvider[] = ['gdrive', 'dropbox', 'onedrive', 'yandex'];

export const ALL_MIGRATION_TARGETS: StorageProvider[] = [
  ...EXTERNAL_PROVIDERS,
  'cassette_s3',
];

export function formatStorageBytes(n?: number): string {
  if (!n || n <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}

export function isProviderLinked(status: StorageStatus | null, provider: StorageProvider): boolean {
  return !!status?.linkedProviders?.includes(provider);
}

/** Cassette Cloud platform (S3 container) is always available unless explicitly disabled. */
export function isCassetteCloudAvailable(status: StorageStatus | null): boolean {
  return status?.cassetteCloud?.available !== false;
}

/** Whether the user may use Cassette Cloud as main project storage (provision, default, migrate). */
export function isEligibleForCassetteProjectStorage(
  status: StorageStatus | null,
  authFallback = true,
): boolean {
  if (status?.cassetteCloud?.eligibleForProjectStorage != null) {
    return status.cassetteCloud.eligibleForProjectStorage;
  }
  if (status?.eligibleForProjectStorage != null) {
    return status.eligibleForProjectStorage;
  }
  if (status?.cassetteCloud?.eligibleForDefault != null) {
    return status.cassetteCloud.eligibleForDefault;
  }
  if (status?.canUseCassetteCloud != null) {
    return status.canUseCassetteCloud;
  }
  return authFallback;
}

/** Linked for UI: Cassette Cloud container is always active; project storage is gated separately. */
export function isProviderLinkedForDisplay(
  status: StorageStatus | null,
  provider: StorageProvider,
): boolean {
  if (provider === 'cassette_s3') {
    return isCassetteCloudAvailable(status);
  }
  return isProviderLinked(status, provider);
}

/** Providers shown in the settings/onboarding grid. */
export function getVisibleProviders(status: StorageStatus | null): StorageProvider[] {
  const enabled = new Set(status?.enabledProviders?.filter((p) => p !== 'none') ?? EXTERNAL_PROVIDERS);
  const providers = EXTERNAL_PROVIDERS.filter((p) => enabled.has(p));
  if (isCassetteCloudAvailable(status) && !providers.includes('cassette_s3')) {
    providers.push('cassette_s3');
  }
  return providers;
}

/** Cloud outline with Cassette tape logo centered inside. */
export function CassetteCloudIcon({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <span className={`relative inline-block shrink-0 ${className}`} aria-hidden>
      <CloudIcon className="w-full h-full text-[var(--brand-from)]" />
      <img
        src="/cassette-logo.svg"
        alt=""
        className="absolute left-1/2 top-[54%] w-[48%] -translate-x-1/2 -translate-y-1/2 object-contain pointer-events-none"
      />
    </span>
  );
}

export function ProviderIcon({
  provider,
  className = 'w-6 h-6',
}: {
  provider: StorageProvider;
  className?: string;
}) {
  switch (provider) {
    case 'gdrive':
      return <SiGoogledrive className={`${className} text-blue-600`} aria-hidden />;
    case 'dropbox':
      return <SiDropbox className={`${className} text-blue-600`} aria-hidden />;
    case 'onedrive':
      return <FaMicrosoft className={`${className} text-blue-600`} aria-hidden />;
    case 'yandex':
      return <FaYandex className={`${className} text-red-600`} aria-hidden />;
    case 'cassette_s3':
      return <CassetteCloudIcon className={className} />;
    default:
      return <CloudIcon className={className} aria-hidden />;
  }
}

export function providerLabel(provider: StorageProvider, t: (key: string, fallback?: string) => string): string {
  if (provider === 'cassette_s3') {
    return t('settings.storage.cassetteCloudName', PROVIDER_LABELS.cassette_s3);
  }
  return t(`storage.providers.${provider}`, PROVIDER_LABELS[provider] ?? provider);
}
